import type {
  IControlSettingData,
  IReportTable,
  PayloadControlChange,
} from '~/presentation/reports/common/type';
import type { IReportCampaign } from '~/modules/campaign-analytics/types';
import type { IOrder, IOrderPageAnalytic } from '../types';

import { FILTER_KEY_TO_CONTROL_IDS } from '../constants/control-filter';
import { OPERATOR_IS } from '~/modules/report-analytic';

import {
  EFilterField,
  EOperatorField,
  EReportControlId,
  ERFilterKey,
} from '~/presentation/reports/common/type';
import { ICampaignType } from '~/modules/campaign';

export const convertOrderItemsToReportTableRows = (orderItems: IOrder[]): IReportTable['rows'] => {
  return orderItems.map((orderItem) => {
    const { events: _events, ...restOrderItem } = orderItem;

    return {
      ...restOrderItem,
      items: orderItem.items ? JSON.parse(orderItem.items as unknown as string) : [],
      experiments: orderItem.experiments
        ? JSON.parse(orderItem.experiments as unknown as string)
        : [],
    };
  });
};

export const getFiltersForLatestOrderListing = ({
  reportCampaign,
  pageAnalytic,
}: {
  reportCampaign?: IReportCampaign;
  pageAnalytic?: IOrderPageAnalytic;
}) => {
  if (!reportCampaign && !pageAnalytic) return undefined;

  if (!reportCampaign) {
    return {
      [EOperatorField.PAGE_OPERATOR]: OPERATOR_IS,
      [EFilterField.SINGLE_PAGE]: pageAnalytic?.path,
    };
  }

  // Price and URL experiments are campaigns, not campaign groups
  if (
    reportCampaign.type === ICampaignType.TEMPLATE ||
    reportCampaign.type === ICampaignType.PRICE ||
    reportCampaign.type === ICampaignType.URL
  ) {
    return {
      [EOperatorField.CAMPAIGN_ITEM_OPERATOR]: OPERATOR_IS,
      [EFilterField.SINGLE_CAMPAIGN]: reportCampaign.id,
    };
  }

  return {
    [EOperatorField.CAMPAIGN_ITEM_OPERATOR]: OPERATOR_IS,
    [EFilterField.GROUP_CAMPAIGN]: reportCampaign.groupId ?? reportCampaign.id,
  };
};

export const checkHasControlFilters = (controlData?: IControlSettingData) => {
  if (!controlData) return false;

  const filterKeys = controlData[EReportControlId.FILTER]?.value;

  if (!Array.isArray(filterKeys) || filterKeys.length === 0) return false;

  // Check if any of the selected filters has an actual value
  return filterKeys.some((filterKey) => {
    const controlIds = FILTER_KEY_TO_CONTROL_IDS[filterKey];
    if (!controlIds) return false;

    return controlIds.some((controlId) => {
      const value = controlData[controlId]?.value;
      if (Array.isArray(value)) return value.length > 0;
      return !!value;
    });
  });
};

export const checkIsChangeFilter = (filterData: PayloadControlChange) => {
  if (!filterData) return false;

  return Object.keys(filterData).some((filterKey) =>
    Object.values(FILTER_KEY_TO_CONTROL_IDS)
      .flat()
      .includes(filterKey as EReportControlId),
  );
};

export const checkIsFilteringByCampaign = (controlData?: IControlSettingData) => {
  if (!controlData) return false;

  const isHasFilterByExperiment = controlData[EReportControlId.FILTER]?.value?.includes(
    ERFilterKey.EXPERIMENT,
  );

  return !!controlData?.[EReportControlId.TAB_CAMPAIGN]?.value && isHasFilterByExperiment;
};
